import { createMalformedApiResponseError } from "./api-client-error";
import { requestApiResponse } from "./request-api-response";
/**
 * Narrow structural check for the canonical register envelope: a non-null,
 * non-array object carrying a boolean `success` flag. Anything else is a
 * malformed API response.
 */
const validateRegisterApiResponse = (data) => {
    if (data === null ||
        typeof data !== "object" ||
        Array.isArray(data) ||
        typeof data.success !== "boolean") {
        throw createMalformedApiResponseError(data);
    }
    return data;
};
const OWNER_ROLE = "Owner";
const SUPERADMIN_ROLE = "SuperAdmin";
const getPayload = (response, message) => {
    if (!response.payload) {
        throw new Error(message);
    }
    return response.payload;
};
const deriveWorkspaceAccess = (roles) => {
    const roleSet = new Set(roles ?? []);
    const available = [];
    const canAccessOwnerWorkspace = roleSet.has(OWNER_ROLE);
    const canAccessSuperadminWorkspace = roleSet.has(SUPERADMIN_ROLE);
    if (canAccessOwnerWorkspace) {
        available.push("owner");
    }
    if (canAccessSuperadminWorkspace) {
        available.push("superadmin");
    }
    return {
        available,
        defaultWorkspace: available.length === 1 ? available[0] : null,
        canAccessOwnerWorkspace,
        canAccessSuperadminWorkspace,
    };
};
const normalizeUserWorkspaceAccess = (user) => ({
    ...user,
    workspaceAccess: deriveWorkspaceAccess(user.roles),
});
const getCurrentUserWithToken = async (client, accessToken) => {
    const response = await client.get("/account/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return normalizeUserWorkspaceAccess(getPayload(response.data, "Current user payload is missing"));
};
const getLoginFlag = (payload, camelKey, pascalKey) => payload[camelKey] === true || payload[pascalKey] === true;
const getLoginEmail = (payload) => {
    return payload.email ?? payload.Email;
};
const hasCamelTokenPair = (payload) => {
    return "accessToken" in payload && "refreshToken" in payload;
};
const hasPascalTokenPair = (payload) => {
    return "AccessToken" in payload && "RefreshToken" in payload;
};
const getTokenPair = (payload) => {
    const accessToken = hasCamelTokenPair(payload)
        ? payload.accessToken
        : hasPascalTokenPair(payload)
            ? payload.AccessToken
            : undefined;
    const refreshToken = hasCamelTokenPair(payload)
        ? payload.refreshToken
        : hasPascalTokenPair(payload)
            ? payload.RefreshToken
            : undefined;
    if (!accessToken || !refreshToken) {
        throw new Error("Login tokens are missing from the response payload");
    }
    return { accessToken, refreshToken };
};
export const getCurrentUser = async (client) => {
    const response = await client.get("/account/me");
    return response.data.payload
        ? {
            ...response.data,
            payload: normalizeUserWorkspaceAccess(response.data.payload),
        }
        : response.data;
};
const resolveLoginResult = async (client, payload) => {
    if (getLoginFlag(payload, "requiresMfaSetup", "RequiresMfaSetup")) {
        const { accessToken, refreshToken } = getTokenPair(payload);
        const user = await getCurrentUserWithToken(client, accessToken);
        return {
            success: false,
            requiresMfaSetup: true,
            email: getLoginEmail(payload) ?? "",
            authResponse: {
                accessToken,
                refreshToken,
                user,
            },
        };
    }
    if (getLoginFlag(payload, "requiresMfa", "RequiresMfa")) {
        return {
            success: false,
            requiresMfa: true,
            email: getLoginEmail(payload) ?? "",
        };
    }
    const { accessToken, refreshToken } = getTokenPair(payload);
    const user = await getCurrentUserWithToken(client, accessToken);
    return {
        success: true,
        authResponse: {
            accessToken,
            refreshToken,
            user,
        },
    };
};
export const login = async (client, data) => {
    const response = await client.post("/account/login", data);
    const payload = getPayload(response.data, "Login response payload is missing");
    return resolveLoginResult(client, payload);
};
/**
 * Social sign-in shares the exact envelope contract with password login:
 * token pair on success, or RequiresMfa / RequiresMfaSetup flags on 200.
 */
const socialSignIn = async (client, path, data) => {
    const response = await client.post(path, data);
    const payload = getPayload(response.data, "Social sign-in response payload is missing");
    return resolveLoginResult(client, payload);
};
export const googleSignIn = async (client, data) => socialSignIn(client, "/auth/google", data);
export const appleSignIn = async (client, data) => socialSignIn(client, "/auth/apple", data);
const linkSocialAccount = async (client, path, data) => {
    const response = await client.post(path, data);
    return response.data;
};
export const linkGoogleAccount = async (client, data) => linkSocialAccount(client, "/auth/google/link", data);
export const linkAppleAccount = async (client, data) => linkSocialAccount(client, "/auth/apple/link", data);
const linkSocialAccountByTicket = async (client, path, data) => {
    const response = await client.post(path, {
        linkTicket: data.linkTicket,
    });
    return response.data;
};
export const linkGoogleAccountByTicket = async (client, data) => linkSocialAccountByTicket(client, "/auth/google/link", data);
export const linkAppleAccountByTicket = async (client, data) => linkSocialAccountByTicket(client, "/auth/apple/link", data);
export const getLinkedAccounts = async (client) => {
    const response = await client.get("/auth/linked-accounts");
    return getPayload(response.data, "Linked accounts response payload is missing");
};
export const unlinkLinkedAccount = async (client, provider) => {
    const response = await client.delete(`/auth/linked-accounts/${provider}`);
    return response.data;
};
/**
 * POST /auth/{provider}/mobile — native social sign-in with a provider-issued
 * ID token (no redirect/code flow). Returns the same login envelope as
 * /account/login, including the RequiresMfa / RequiresMfaSetup 200 variants.
 */
const socialSignInMobile = async (client, path, data) => {
    const response = await client.post(path, data);
    const payload = getPayload(response.data, "Social sign-in response payload is missing");
    return resolveLoginResult(client, payload);
};
export const googleSignInMobile = async (client, data) => socialSignInMobile(client, "/auth/google/mobile", {
    idToken: data.idToken,
    platform: data.platform,
});
export const appleSignInMobile = async (client, data) => socialSignInMobile(client, "/auth/apple/mobile", {
    idToken: data.idToken,
    nonce: data.nonce,
    fullName: data.fullName,
});
/**
 * POST /auth/{provider}/link (authorized) with a native ID token — attach a
 * provider identity to the current account. Resolves on 200; a 409
 * AccountConflict surfaces as an ApiClientError (code "AccountConflict" /
 * status 409).
 */
const linkSocialAccountMobile = async (client, path, data) => {
    const response = await client.post(path, {
        idToken: data.idToken,
    });
    return response.data;
};
export const linkGoogleAccountMobile = async (client, data) => linkSocialAccountMobile(client, "/auth/google/link", data);
export const linkAppleAccountMobile = async (client, data) => linkSocialAccountMobile(client, "/auth/apple/link", data);
/**
 * Canonical register wire body: a fresh allowlisted object so client-only
 * runtime extras (e.g. confirmPassword) can never leak onto the wire.
 * Blank optional strings are omitted. The caller's payload is never mutated.
 */
const toRegisterWireBody = (data) => {
    const body = {
        email: data.email,
        password: data.password,
    };
    for (const key of [
        "firstName",
        "lastName",
        "phoneNumber",
        "emergencyContactName",
        "emergencyContactPhone",
        "language",
    ]) {
        const value = data[key];
        if (typeof value === "string" && value.trim().length > 0) {
            body[key] = value;
        }
    }
    return body;
};
export const register = async (client, data) => {
    await requestApiResponse(() => client.post("/account/register", toRegisterWireBody(data)), { validateApiResponse: validateRegisterApiResponse });
};
export const verifyMfaLogin = async (client, data) => {
    const response = await client.post("/account/login/mfa", data);
    const payload = getPayload(response.data, "MFA login payload is missing");
    const { accessToken, refreshToken } = getTokenPair(payload);
    const user = await getCurrentUserWithToken(client, accessToken);
    return {
        accessToken,
        refreshToken,
        user,
    };
};
export const setupMfa = async (client) => {
    const response = await client.post("/mfa/setup");
    return getPayload(response.data, "MFA setup payload is missing");
};
export const verifyMfaSetup = async (client, code) => {
    const response = await client.post("/mfa/verify-setup", { code });
    return getPayload(response.data, "MFA setup verification payload is missing");
};
export const disableMfa = async (client, code) => {
    const response = await client.post("/mfa/disable", { code });
    return getPayload(response.data, "Disable MFA payload is missing");
};
export const sendVerificationCode = async (client) => {
    const response = await client.post("/account/send-verification-code");
    return response.data;
};
export const verifyCode = async (client, code) => {
    const response = await client.post("/account/verify-code", { code });
    return response.data;
};
export const logout = async (client) => {
    await client.post("/auth/logout");
};
export const createUser = async (client, data) => {
    const response = await client.post("/account/user/create", data);
    return getPayload(response.data, "Create user payload is missing");
};
export const updateCurrentUser = async (client, data) => {
    const response = await client.put("/account/user/update", data);
    return response.data;
};
export const updateCurrentUserAvatar = async (client, avatarUrl) => {
    const response = await client.put("/account/user/avatar", { avatarUrl });
    return response.data;
};
export const deleteCurrentUser = async (client, payload) => {
    const response = payload?.appleAuthorizationCode
        ? await client.delete("/account/me", { data: payload })
        : await client.delete("/account/me");
    return response.data;
};
export const createAccountApi = (client) => ({
    getCurrentUser: () => getCurrentUser(client),
    getLinkedAccounts: () => getLinkedAccounts(client),
    unlinkLinkedAccount: (provider) => unlinkLinkedAccount(client, provider),
    login: (data) => login(client, data),
    googleSignIn: (data) => googleSignIn(client, data),
    appleSignIn: (data) => appleSignIn(client, data),
    linkGoogleAccount: (data) => linkGoogleAccount(client, data),
    linkAppleAccount: (data) => linkAppleAccount(client, data),
    linkGoogleAccountByTicket: (data) => linkGoogleAccountByTicket(client, data),
    linkAppleAccountByTicket: (data) => linkAppleAccountByTicket(client, data),
    googleSignInMobile: (data) => googleSignInMobile(client, data),
    appleSignInMobile: (data) => appleSignInMobile(client, data),
    linkGoogleAccountMobile: (data) => linkGoogleAccountMobile(client, data),
    linkAppleAccountMobile: (data) => linkAppleAccountMobile(client, data),
    register: (data) => register(client, data),
    verifyMfaLogin: (data) => verifyMfaLogin(client, data),
    setupMfa: () => setupMfa(client),
    verifyMfaSetup: (code) => verifyMfaSetup(client, code),
    disableMfa: (code) => disableMfa(client, code),
    sendVerificationCode: () => sendVerificationCode(client),
    verifyCode: (code) => verifyCode(client, code),
    logout: () => logout(client),
    createUser: (data) => createUser(client, data),
    updateCurrentUser: (data) => updateCurrentUser(client, data),
    updateCurrentUserAvatar: (avatarUrl) => updateCurrentUserAvatar(client, avatarUrl),
    deleteCurrentUser: (payload) => deleteCurrentUser(client, payload),
});
